import { useMemo } from 'react'
import { DateTime } from 'luxon'
import {
  isDefined,
  isEmpty,
  isNotDefined,
  type AdvancedDateRangeOffset,
  type DashboardFilter,
  type DashboardWidget,
  type ReportColumnDisplay,
  type ReportDateRange,
  type ReportDateRangePreset,
  type ReportDateRangeUnit,
  type ReportKpiGrouping,
  type ReportKpiInput,
  type ReportKpiSuccess,
  type ReportTimePeriod,
} from '@terros/common'
import {
  getWidgetGrouping,
  usePreference,
  usePrimaryTeam,
  useProfile,
  useReportKpiV2,
  type GetHookResponse,
} from '@front/model'
import { toDashboardReportFilter } from './dashboardFilter'

export type DashboardWidgetKpiState = GetHookResponse<ReportKpiSuccess> & {
  input: ReportKpiInput | undefined
}

export function useDashboardWidgetKpi(
  widget: DashboardWidget,
  dashboardDateRange: ReportDateRange | undefined,
  dashboardFilter: DashboardFilter | undefined
): DashboardWidgetKpiState {
  const { user } = useProfile()
  const currentTeamId = usePrimaryTeam()
  const { data: managerBasedTeams = false } = usePreference('managerBasedTeams')
  const input = useMemo((): ReportKpiInput | undefined => {
    if (isEmpty(widget.metrics)) return undefined
    const kpis = toReportColumnDisplays(widget)
    const grouping = toReportGrouping(widget, managerBasedTeams)
    const dateFilter = toDateRangeFilter(dashboardDateRange)
    return {
      grouping,
      kpis,
      filter: toDashboardReportFilter({
        dateFilter,
        dashboardFilter,
        currentUserId: user?.userId,
        currentTeamId,
      }),
    }
  }, [currentTeamId, dashboardDateRange, dashboardFilter, managerBasedTeams, user?.userId, widget])

  const state = useReportKpiV2(input ?? { grouping: { type: 'company' }, kpis: [] }, isDefined(input))

  return {
    ...state,
    input,
  }
}

function toReportColumnDisplays(widget: DashboardWidget): ReportColumnDisplay[] {
  const metricColumns = widget.metrics.map((metric) => ({ value: metric.kpi }))
  if (widget.type === 'number') return metricColumns
  return [{ value: 'groupBy' }, ...metricColumns]
}

export function toReportGrouping(widget: DashboardWidget, managerBasedTeams: boolean): ReportKpiGrouping {
  switch (widget.type) {
    case 'number':
      return { type: 'company' }
    case 'leaderboard':
    case 'table':
      return toGroupedWidgetReportGrouping(widget, managerBasedTeams)
    case 'line':
      return { type: 'time', period: toTimePeriod(widget.resolution) }
    case 'game':
    case 'goal':
      return { type: 'company' }
  }
}

function toGroupedWidgetReportGrouping(
  widget: Extract<DashboardWidget, { type: 'leaderboard' | 'table' }>,
  managerBasedTeams: boolean
): ReportKpiGrouping {
  const grouping = getWidgetGrouping(widget)
  if (!managerBasedTeams) return grouping
  if (grouping.type === 'team') return { type: 'manager', level: grouping.level }
  return grouping
}

function toTimePeriod(resolution: Extract<DashboardWidget, { type: 'line' }>['resolution']): ReportTimePeriod {
  if (isNotDefined(resolution) || resolution === 'auto') return 'day'
  return resolution
}

function toDateRangeFilter(dateRange: ReportDateRange | undefined): ReportKpiInput['filter'] {
  if (isNotDefined(dateRange)) return toRelativeDateRangeFilter(30, 'days')

  switch (dateRange.type) {
    case 'fixed':
      return { startDate: toMillis(dateRange.startTime), endDate: toMillis(dateRange.endTime) }
    case 'relative':
      return toRelativeDateRangeFilter(dateRange.value, dateRange.unit)
    case 'preset':
      return toPresetDateRangeFilter(dateRange.period)
    case 'advanced':
      return toAdvancedDateRangeFilter(dateRange.start, dateRange.end)
  }
}

function toRelativeDateRangeFilter(value: number, unit: ReportDateRangeUnit): ReportKpiInput['filter'] {
  const endDate = DateTime.utc().endOf('day')
  const startDate = endDate.minus({ [unit]: value }).startOf('day')
  return { startDate: startDate.toMillis(), endDate: endDate.toMillis() }
}

function toPresetDateRangeFilter(period: ReportDateRangePreset): ReportKpiInput['filter'] {
  const now = DateTime.utc()
  switch (period) {
    case 'today':
      return { startDate: now.startOf('day').toMillis(), endDate: now.endOf('day').toMillis() }
    case 'yesterday':
      return {
        startDate: now.minus({ days: 1 }).startOf('day').toMillis(),
        endDate: now.minus({ days: 1 }).endOf('day').toMillis(),
      }
    case 'this_week':
      return { startDate: now.startOf('week').toMillis(), endDate: now.endOf('day').toMillis() }
    case 'this_month':
      return { startDate: now.startOf('month').toMillis(), endDate: now.endOf('day').toMillis() }
    case 'this_quarter':
      return { startDate: now.startOf('quarter').toMillis(), endDate: now.endOf('day').toMillis() }
    case 'this_year':
      return { startDate: now.startOf('year').toMillis(), endDate: now.endOf('day').toMillis() }
    case 'year_to_date':
      return { startDate: now.startOf('year').toMillis(), endDate: now.endOf('day').toMillis() }
    case 'last_week':
      return {
        startDate: now.minus({ weeks: 1 }).startOf('week').toMillis(),
        endDate: now.minus({ weeks: 1 }).endOf('week').toMillis(),
      }
    case 'last_month':
      return {
        startDate: now.minus({ months: 1 }).startOf('month').toMillis(),
        endDate: now.minus({ months: 1 }).endOf('month').toMillis(),
      }
    case 'last_quarter':
      return {
        startDate: now.minus({ quarters: 1 }).startOf('quarter').toMillis(),
        endDate: now.minus({ quarters: 1 }).endOf('quarter').toMillis(),
      }
    case 'last_year':
      return {
        startDate: now.minus({ years: 1 }).startOf('year').toMillis(),
        endDate: now.minus({ years: 1 }).endOf('year').toMillis(),
      }
    case 'all_time':
    default:
      return undefined
  }
}

function toAdvancedDateRangeFilter(
  start: AdvancedDateRangeOffset | undefined,
  end: AdvancedDateRangeOffset | undefined
): ReportKpiInput['filter'] {
  const now = DateTime.utc()
  const startDate = start
    ? now[start.direction === 'minus' ? 'minus' : 'plus']({ [start.unit]: start.value }).startOf('day')
    : now.startOf('day')
  const endDate = end
    ? now[end.direction === 'minus' ? 'minus' : 'plus']({ [end.unit]: end.value }).endOf('day')
    : now.endOf('day')
  return { startDate: startDate.toMillis(), endDate: endDate.toMillis() }
}

function toMillis(value: string | number): number {
  if (typeof value === 'number') return value
  return DateTime.fromISO(value).toMillis()
}
